import { PIANO_HIT_LINE_Y_FRAC, PIANO_TILE_HEIGHT_FRAC } from '@shared/pianoChart';
import type { ManiaLane } from '@shared/maniaNote';

/**
 * Centralised tuning + layout constants for the Piano render subsystem.
 * Owning these in one file keeps the magic numbers searchable and lets
 * one change (e.g. tile height, lane gap) ripple to every draw module
 * without hunting through five files.
 *
 * The two values that the engine cares about
 * (`HIT_LINE_Y_FRAC` + `TILE_HEIGHT_FRAC`) are re-exported from
 * `pianoChart.ts` so the engine's geometric perfect window stays in
 * lockstep with the renderer's visual.
 */

export const HIT_LINE_Y_FRAC = PIANO_HIT_LINE_Y_FRAC;

/**
 * Maximum CSS width of the playing field (lane stack + horizontal
 * padding). On wider canvases the field is centred horizontally and
 * the leftover space becomes a dark margin. Set to 480 to land
 * lanes ≈ 105 CSS px wide on a typical canvas — narrow enough that
 * tall tiles read as 1.4–2.0× tall, while staying wide enough for
 * comfortable touch targets on tablets.
 */
export const PLAYING_FIELD_MAX_WIDTH_PX = 480;

/**
 * Tile height in CSS px is `PIANO_TILE_HEIGHT_FRAC × canvasHeight`,
 * clamped to [TILE_HEIGHT_MIN_PX, TILE_HEIGHT_MAX_PX]. The clamps
 * keep tiles legible on extreme aspect ratios at the cost of a small
 * drift between engine and visual at those extremes; the typical
 * playable range is exact.
 */
export const TILE_HEIGHT_FRAC = PIANO_TILE_HEIGHT_FRAC;
export const TILE_HEIGHT_MIN_PX = 150;
export const TILE_HEIGHT_MAX_PX = 240;

/**
 * Padding either side of the lane stack within the playing field.
 * Gives the HUD (score, combo) a clean column to sit in and stops
 * tiles touching the field's inner edges.
 */
export const LANE_STACK_HORIZONTAL_PAD_PX = 24;

/** Visual gap between adjacent lanes. */
export const LANE_GAP_PX = 4;

export const HP_BAR_HEIGHT_PX = 8;
export const HP_BAR_TOP_PX = 16;

/**
 * HP fraction below which the bar pulses its red fill via a glow
 * shadow. Set distinctly tighter than the colour-flip threshold
 * (0.4) so the pulse reads as "you are in real danger" rather than
 * cosmetic, while leaving a quiet warning zone between 0.3 and 0.4.
 */
export const HP_DANGER_PULSE_THRESHOLD = 0.3;

/** Tile corner radius for the rounded-rect render. */
export const TILE_CORNER_RADIUS_PX = 8;

/**
 * Fraction of `perfectOverlapBeats` that the danger pulse occupies,
 * starting from the LATE end. At 0.5 the pulse fires for the second
 * half of the tile's on-bar window — a "your last chance" cue without
 * crying wolf as soon as the tile arrives. The renderer derives the
 * actual beat-width from `perfectOverlapBeats × DANGER_WINDOW_FRAC`
 * so this single knob stays coupled to the engine's perfect span.
 */
export const DANGER_WINDOW_FRAC = 0.5;

/** Combo at which the background bloom starts cycling through combo-tier hues. */
export const COMBO_BG_TIER_THRESHOLD = 10;

/** Approximate y of the score-HUD readout in canvas px; score pops arc toward this. The x is derived from layout (anchored to the playing field's right edge). */
export const SCORE_HUD_TARGET_TOP_PAD_PX = 56;

/** Base font size for the combo readout in CSS px before scale / pulse modifiers. */
export const COMBO_HUD_BASE_FONT_PX = 28;

/** Minimum combo at which the under-text streak underline appears. Below this the readout is just text. */
export const COMBO_UNDERLINE_MIN = 5;

/** Lifetime (seconds) of the "GO!" banner the renderer pops the frame the lead-in ends. */
export const GO_BANNER_LIFE = 0.6;

/** Hit-stop frozen-frame state caps so a sustained perfect stream can't permanently freeze. */
export const HIT_STOP_MAX_MS = 80;

/**
 * Cap of long-lived particles so a sustained stream can't bloat the
 * draw list. Sized to comfortably hold a 14 perfect/sec stream where
 * each perfect spawns ~27 particles (main burst + sparkle layer +
 * upward spark column) at average ~0.7s lifetimes.
 */
export const PARTICLE_HARD_CAP = 600;

/**
 * Pale-aqua accent for the second sparkle layer fired on perfect hits.
 * Sits visually above the saturated mint of the main burst so the eye
 * reads "two layers of energy" rather than a uniform blob.
 */
export const PERFECT_SPARKLE_COLOUR = '#9bf6e6';

/**
 * Default key labels rendered below each lane at the hit line. These
 * are the static defaults — actual keybinds may differ if the player
 * rebound them, and the labels read the live prefs at session start
 * (see `loadPianoKeyLabels()` in `pianoSession.ts`) so the displayed
 * letter always matches what the player has bound.
 */
export const DEFAULT_KEY_LABELS: Record<ManiaLane, string> = {
  0: 'Q',
  1: 'W',
  2: 'E',
  3: 'R',
};

/**
 * Combo-tier hue cycle for the background bloom + hit-line glow.
 * Hues are deliberately well-separated so the eye reads each tier
 * crossover as a distinct "the music levelled up" cue.
 */
export const COMBO_BLOOM_PALETTE = ['#16e0ff', '#3bff9d', '#ffd84d', '#ff2bd6'] as const;
